import { Button, Icon, QualState, RenewalTimeline, Screen, ScreenHeader, TierBadge, humanize } from '@garnet/ui';
import type { RecordStateVM, VerticalData } from '../data/types.js';
import type { Route } from '../nav.js';

/* one row of the "Needs you" triage — count · label · most-urgent fact · route */
function Triage({
  n,
  label,
  detail,
  tone,
  last,
  onClick,
}: {
  n: number;
  label: string;
  detail: string;
  tone?: string;
  last?: boolean;
  onClick: () => void;
}) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => e.key === 'Enter' && onClick()}
      style={{
        display: 'grid',
        gridTemplateColumns: '34px 1fr 16px',
        gap: 12,
        alignItems: 'start',
        padding: '13px 18px',
        cursor: 'pointer',
        borderBottom: last ? 0 : '1px solid var(--rule)',
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = '#fafaf8')}
      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
    >
      <span
        style={{
          fontFamily: 'var(--mono)',
          fontSize: 25,
          fontWeight: 600,
          lineHeight: 1,
          marginTop: 1,
          color: tone || 'var(--ink)',
        }}
      >
        {n}
      </span>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 12.5, fontWeight: 600 }}>{label}</div>
        <div
          style={{
            fontFamily: 'var(--mono)',
            fontSize: 11,
            color: 'var(--muted)',
            marginTop: 3,
            lineHeight: 1.45,
          }}
        >
          {detail}
        </div>
      </div>
      <Icon name="chevron" size={15} style={{ color: 'var(--muted-2)', marginTop: 6 }} />
    </div>
  );
}

export function Dashboard({ data, go }: { data: VerticalData; go: (r: Route) => void }) {
  const { suppliers, cases, credentials, inbox, copy, hero } = data;
  const byState = (s: RecordStateVM) => suppliers.filter((x) => x.state === s).length;
  const total = suppliers.length;

  // release posture — the full qualification distribution, sums to `total`
  const states: Array<{ key: RecordStateVM; label: string; n: number; c: string }> = [
    { key: 'clear', label: 'clear', n: byState('clear'), c: 'var(--verified)' },
    { key: 'flags', label: 'with flags', n: byState('flags'), c: 'var(--attest)' },
    { key: 'verifying', label: 'verifying', n: byState('verifying'), c: 'var(--neutral)' },
    { key: 'blocked', label: 'blocked', n: byState('blocked'), c: 'var(--flag)' },
    { key: 'expired', label: 'expired', n: byState('expired'), c: 'var(--flag-ink)' },
  ];
  const cannot = byState('blocked') + byState('expired');

  // work queues — derived, prioritised
  const openCs = cases.filter((c) => c.status !== 'resolved' && c.status !== 'dismissed');
  const blocking = openCs.filter((c) => c.severity === 'blocking').length;
  const high = openCs.filter((c) => c.severity === 'high').length;
  const breached = openCs.some((c) => c.slaHrs < 0);
  const caseParts: string[] = [];
  if (blocking) caseParts.push(blocking + ' blocking' + (breached ? ' · SLA breached' : ''));
  if (high) caseParts.push(high + ' high');
  const caseDetail = caseParts.join(' · ') || 'all within SLA';

  const lapsing = credentials.filter((c) => c.st !== 'valid');
  const expiredN = lapsing.filter((c) => c.st === 'expired').length;
  const soonest = lapsing.filter((c) => c.days >= 0).sort((a, b) => a.days - b.days)[0];
  const shortName = (n: string) =>
    n
      .replace(/ (FZE|GmbH|AG|Ltd|Pvt Ltd|BV|Supplies|Medical Devices|Diagnostics).*$/, '')
      .trim();
  const lapseDetail =
    (soonest
      ? 'soonest ' + soonest.days + ' d — ' + soonest.cred + ' · ' + shortName(soonest.supplier)
      : 'none upcoming') + (expiredN ? ' · ' + expiredN + ' expired' : '');

  const toConfirm = inbox.filter((d) => d.status !== 'ready');
  const lowConf = inbox.filter(
    (d) => d.status === 'translate' || (d.fields || []).some((f) => f.low),
  ).length;
  const docDetail = 'inbox · ' + (lowConf ? lowConf + ' low-confidence' : toConfirm.length + ' pending');

  const activity = copy.activity;

  return (
    <Screen>
      <ScreenHeader
        eyebrow="§ Dashboard"
        title="Supplier portfolio"
        actions={
          <Button variant="primary" icon="plus" onClick={() => go('flow')}>
            Start a diligence
          </Button>
        }
      />

      {/* posture + triage band */}
      <div
        className="panel"
        style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr' }}
      >
        {/* release posture */}
        <div style={{ padding: '16px 20px 18px', borderRight: '1px solid var(--rule)' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              marginBottom: 14,
            }}
          >
            <span
              style={{
                fontFamily: 'var(--mono)',
                fontSize: 10.5,
                letterSpacing: '.08em',
                textTransform: 'uppercase',
                color: 'var(--muted)',
              }}
            >
              Release posture
            </span>
            <span
              style={{
                fontFamily: 'var(--mono)',
                fontSize: 11,
                color: 'var(--muted)',
                whiteSpace: 'nowrap',
              }}
            >
              {total} suppliers
            </span>
          </div>

          <div style={{ display: 'flex', gap: 3, height: 20, marginBottom: 13 }}>
            {states
              .filter((s) => s.n > 0)
              .map((s) => (
                <div
                  key={s.key}
                  title={s.label + ': ' + s.n}
                  style={{ flex: s.n + ' 1 0', minWidth: 16, background: s.c, borderRadius: 2 }}
                />
              ))}
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '7px 18px' }}>
            {states.map((s) => (
              <span
                key={s.key}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 7, opacity: s.n ? 1 : 0.4 }}
              >
                <span style={{ width: 8, height: 8, borderRadius: 2, background: s.c, flex: 'none' }} />
                <span style={{ fontFamily: 'var(--mono)', fontSize: 12.5, fontWeight: 600 }}>{s.n}</span>
                <span style={{ fontFamily: 'var(--mono)', fontSize: 11.5, color: 'var(--muted)' }}>
                  {s.label}
                </span>
              </span>
            ))}
          </div>

          <div
            style={{
              marginTop: 15,
              paddingTop: 13,
              borderTop: '1px solid var(--rule-soft)',
              fontSize: 12.5,
              color: 'var(--muted)',
              lineHeight: 1.5,
            }}
          >
            <b style={{ color: 'var(--verified-ink)', fontWeight: 600 }}>
              {byState('clear')} cleared to release
            </b>{' '}
            · {byState('flags')} releasable with conditions ·{' '}
            <b style={{ color: 'var(--flag-ink)', fontWeight: 600 }}>{cannot} cannot release</b> until
            cleared.
          </div>
        </div>

        {/* needs you */}
        <div>
          <div
            style={{
              fontFamily: 'var(--mono)',
              fontSize: 10.5,
              letterSpacing: '.08em',
              textTransform: 'uppercase',
              color: 'var(--muted)',
              padding: '16px 18px 11px',
            }}
          >
            Needs you
          </div>
          <Triage
            n={openCs.length}
            label="Open cases"
            detail={caseDetail}
            tone={blocking ? 'var(--flag)' : 'var(--attest-ink)'}
            onClick={() => go('cases')}
          />
          <Triage
            n={lapsing.length}
            label="Credentials lapsing"
            detail={lapseDetail}
            tone={expiredN ? 'var(--flag)' : 'var(--attest-ink)'}
            onClick={() => go('renewals')}
          />
          <Triage
            n={toConfirm.length}
            label="Docs to confirm"
            detail={docDetail}
            tone="var(--ink)"
            last
            onClick={() => go('inbox')}
          />
        </div>
      </div>

      <div className="split-pane">
        {/* portfolio */}
        <div className="panel">
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '12px 16px',
              borderBottom: '1px solid var(--ink)',
            }}
          >
            <span
              style={{
                fontFamily: 'var(--mono)',
                fontSize: 10.5,
                letterSpacing: '.08em',
                textTransform: 'uppercase',
                color: 'var(--muted)',
              }}
            >
              Suppliers · {suppliers.length}
            </span>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--muted)' }}>
              tier · state · coverage
            </span>
          </div>
          {suppliers.map((s) => {
            const isHero = s.id === hero.supplierId;
            return (
              <div
                key={s.id}
                onClick={() => isHero && go('record')}
                role={isHero ? 'button' : undefined}
                tabIndex={isHero ? 0 : -1}
                onKeyDown={(e) => isHero && e.key === 'Enter' && go('record')}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr auto',
                  gap: 12,
                  alignItems: 'center',
                  padding: '12px 16px',
                  borderBottom: '1px solid var(--rule)',
                  cursor: isHero ? 'pointer' : 'default',
                  background: 'var(--panel)',
                }}
                onMouseEnter={(e) => isHero && (e.currentTarget.style.background = '#fafaf8')}
                onMouseLeave={(e) => isHero && (e.currentTarget.style.background = 'var(--panel)')}
              >
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontWeight: 500, fontSize: 13.5 }}>{s.name}</span>
                    {isHero && (
                      <span
                        style={{
                          fontFamily: 'var(--mono)',
                          fontSize: 9.5,
                          color: 'var(--muted)',
                          border: '1px solid var(--rule)',
                          borderRadius: 2,
                          padding: '0 4px',
                        }}
                      >
                        open →
                      </span>
                    )}
                  </div>
                  <div
                    style={{
                      fontFamily: 'var(--mono)',
                      fontSize: 11,
                      color: 'var(--muted)',
                      marginTop: 3,
                    }}
                  >
                    {s.flag} {s.place} · {s.machineCoverage}% machine
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <TierBadge level={s.tier} sm showLabel={false} />
                  <QualState state={s.state} />
                </div>
              </div>
            );
          })}
        </div>

        {/* activity + next lapse */}
        <div style={{ display: 'grid', gap: 16 }}>
          <div className="panel">
            <div
              style={{
                fontFamily: 'var(--mono)',
                fontSize: 10.5,
                letterSpacing: '.08em',
                textTransform: 'uppercase',
                color: 'var(--muted)',
                padding: '12px 16px',
                borderBottom: '1px solid var(--ink)',
              }}
            >
              Recent activity
            </div>
            {activity.map((a, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  gap: 10,
                  padding: '11px 16px',
                  borderBottom: i < activity.length - 1 ? '1px solid var(--rule)' : 0,
                }}
              >
                <span
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: '50%',
                    marginTop: 5,
                    flex: 'none',
                    background:
                      a.tone === 'm'
                        ? 'var(--verified)'
                        : a.tone === 'a'
                          ? 'var(--attest)'
                          : 'var(--flag)',
                  }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 500 }}>{humanize(a.t)}</div>
                  <div style={{ fontSize: 12.5, marginTop: 1 }}>{a.who}</div>
                  <div
                    style={{
                      fontFamily: 'var(--mono)',
                      fontSize: 11,
                      color: 'var(--muted)',
                      marginTop: 2,
                    }}
                  >
                    {a.meta}
                  </div>
                </div>
                <span style={{ fontFamily: 'var(--mono)', fontSize: 10.5, color: 'var(--muted)' }}>
                  {a.ago}
                </span>
              </div>
            ))}
          </div>
          <div className="panel" style={{ padding: '16px 18px' }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                marginBottom: 12,
              }}
            >
              <span
                style={{
                  fontFamily: 'var(--mono)',
                  fontSize: 10.5,
                  letterSpacing: '.08em',
                  textTransform: 'uppercase',
                  color: 'var(--muted)',
                }}
              >
                Next to lapse
              </span>
              <Button sm variant="ghost" icon="clock" onClick={() => go('renewals')}>
                Renewals
              </Button>
            </div>
            {copy.dashLapse.map((l, i) => (
              <div key={l.name}>
                {i > 0 && <div style={{ height: 12 }} />}
                <RenewalTimeline name={l.name} meta={l.meta} state={l.st} pct={l.pct} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </Screen>
  );
}
